import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { useCart } from "../context/CartContext";
import {
  FaTruck,
  FaStore,
  FaMotorcycle,
  FaCheckCircle,
  FaCircle,
  FaTimes,
  FaLocationArrow,
  FaEdit,
  FaTrash,
  FaPlus,
} from "react-icons/fa";
import styles from "./DeliveryOptionsPage.module.css";

import usFlagImg from "../assets/us.png";

function DeliveryOptionsPage() {
  const { setDeliveryOption } = useCart();
  const navigate = useNavigate();

  const [selectedMethodId, setSelectedMethodId] = useState("pickup");
  const [selectedLocationId, setSelectedLocationId] = useState("bole");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [savedAddresses, setSavedAddresses] = useState(() => {
    const saved = localStorage.getItem("dukamoSavedAddresses");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem(
      "dukamoSavedAddresses",
      JSON.stringify(savedAddresses)
    );
  }, [savedAddresses]);

  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});

  const ADDIS_LOCATIONS = [
    "Bole",
    "Goro",
    "Sarbet",
    "Piassa",
    "Mexico",
    "Kazanchis",
    "4 Kilo",
    "6 Kilo",
    "Megenagna",
    "CMC",
    "Ayat",
    "Kality",
    "Akaki",
    "Lebu",
    "Jemo",
    "Tor Hailoch",
    "Lideta",
    "Bambis",
    "Urael",
    "Atlas",
    "Gerji",
    "Summit",
    "Yeka",
    "Gulele",
    "Nifas Silk",
  ];

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapQuery, setMapQuery] = useState("Addis Ababa");

  const [newAddress, setNewAddress] = useState({
    address: "",
    fullName: "",
    countryCode: "+251",
    phone: "",
    homeNumber: "",
    detail: "",
  });

  // --- 💎 UPDATED DELIVERY METHODS (Betty's Rates) ---
  const deliveryMethods = [
    {
      category: "Dukamo Coffee Delivery",
      desc: "Please fill out the form below. Choose your delivery location.",
      options: [
        {
          id: "pickup",
          title: "Dukamo Pick Up",
          subtitle: "nearby Dukamo office.",
          icon: <FaStore color="#ff9800" />,
          price: 0,
        },
        {
          id: "delivery_addis",
          title: "Addis Ababa Delivery",
          subtitle: "2-3 Days Only For A.A (300 Br)",
          icon: <FaMotorcycle color="#4caf50" />,
          price: 300, // UPDATED
        },
        {
          id: "delivery_hawassa",
          title: "Hawassa Delivery",
          subtitle: "3-5 days (200 Br)",
          icon: <FaTruck color="#00bcd4" />,
          price: 200, // UPDATED
        },
      ],
    },
    {
      category: "International Delivery",
      options: [
        {
          id: "delivery_us",
          title: "United States Delivery",
          subtitle: "5-15 Days ($15 USD)",
          customIcon: usFlagImg,
          price: 2350, // UPDATED: $15 * 156.14 Rate
        },
      ],
    },
  ];

  const pickupLocations = [
    {
      id: "bole",
      title: "Dukamo Coffee Bole",
      address: "Mafi City Mall, Addis Ababa, Ethiopia",
    },
    {
      id: "bherawi",
      title: "Dukamo Bherawi",
      address: "Nile Insurance Building, Addis Ababa, Ethiopia",
    },
    {
      id: "sarbet",
      title: "Buncho coffee Jacros",
      address: "Salite Mehret Rd, Addis Ababa, Ethiopia",
    },
  ];

  const filteredAddresses = savedAddresses.filter((addr) => {
    const addrTag = addr.cityTag || "delivery_addis";
    return addrTag === selectedMethodId;
  });

  // --- HANDLERS ---
  const handleAddressSearch = (e) => {
    const val = e.target.value;
    setNewAddress({ ...newAddress, address: val });
    if (selectedMethodId === "delivery_addis") {
      if (val.length > 0) {
        const filtered = ADDIS_LOCATIONS.filter((loc) =>
          loc.toLowerCase().includes(val.toLowerCase())
        );
        setSuggestions(filtered);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (location) => {
    setNewAddress({ ...newAddress, address: location });
    setMapQuery(`${location}, Addis Ababa`);
    setShowSuggestions(false);
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapQuery(
            `${position.coords.latitude},${position.coords.longitude}`
          );
          setNewAddress({ ...newAddress, address: "Current Location" });
        },
        () => alert("Unable to retrieve location.")
      );
    } else {
      alert("Geolocation not supported.");
    }
  };

  const handleDetailChange = (e) =>
    setNewAddress({ ...newAddress, detail: e.target.value });
  const handlePhoneChange = (e) =>
    setNewAddress({ ...newAddress, phone: e.target.value.replace(/\D/g, "") });

  const openNewAddressModal = () => {
    setEditingId(null);
    setNewAddress({
      address: "",
      fullName: "",
      countryCode: selectedMethodId === "delivery_us" ? "+1" : "+251",
      phone: "",
      homeNumber: "",
      detail: "",
    });
    setMapQuery(
      selectedMethodId === "delivery_hawassa"
        ? "Hawassa, Ethiopia"
        : selectedMethodId === "delivery_us"
        ? "United States"
        : "Addis Ababa"
    );
    setShowAddressModal(true);
  };

  const handleEditAddress = (e, addr) => {
    e.stopPropagation();
    setEditingId(addr.id);
    setNewAddress({
      address: addr.addressName,
      fullName: addr.fullName,
      countryCode: addr.countryCode || "+251",
      phone: addr.phone,
      homeNumber: addr.homeNumber,
      detail: addr.detail,
    });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = (e, id) => {
    e.stopPropagation();
    const updated = savedAddresses.filter((addr) => addr.id !== id);
    setSavedAddresses(updated);
    if (selectedAddressId === id) setSelectedAddressId(null);
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();
    if (newAddress.fullName.trim().split(/\s+/).length < 2) {
      setErrors({ fullName: "Please enter your full name." });
      return;
    }
    let defaultLabel = "My Location";
    if (selectedMethodId === "delivery_hawassa")
      defaultLabel = "My Hawassa Location";
    if (selectedMethodId === "delivery_us") defaultLabel = "My US Location";

    const entry = {
      id: editingId || Date.now().toString(),
      addressName: newAddress.address || defaultLabel,
      ...newAddress,
      cityTag: selectedMethodId,
    };

    if (editingId) {
      setSavedAddresses(
        savedAddresses.map((a) => (a.id === editingId ? entry : a))
      );
    } else {
      setSavedAddresses([...savedAddresses, entry]);
      setSelectedAddressId(entry.id);
    }
    setShowAddressModal(false);
    setErrors({});
    setEditingId(null);
  };

  const handleContinue = () => {
    let finalChoice = null;
    const allMethods = deliveryMethods.flatMap((cat) => cat.options);
    const method = allMethods.find((m) => m.id === selectedMethodId);

    if (!method) return;

    const cleanMethod = {
      id: method.id,
      name: method.title, // 'name' field ensures backend compatibility
      price: method.price,
    };

    if (selectedMethodId === "pickup") {
      const location = pickupLocations.find((l) => l.id === selectedLocationId);

      finalChoice = {
        ...cleanMethod,
        title: location.title,
        details: `${location.title} - ${location.address}`,
      };
    } else if (
      ["delivery_addis", "delivery_hawassa", "delivery_us"].includes(
        selectedMethodId
      )
    ) {
      if (!selectedAddressId) {
        alert("Please select or create a delivery address.");
        return;
      }
      const address = savedAddresses.find((a) => a.id === selectedAddressId);
      finalChoice = {
        ...cleanMethod,
        title: method.title,
        details: `${method.title} - Deliver to: ${address.fullName}, ${address.addressName}`,
        addressData: address,
      };
    }

    setDeliveryOption(finalChoice);
    navigate("/checkout");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.pageContainer}>
      <Navbar />
      {showAddressModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalMapColumn}>
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  mapQuery
                )}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="100%"
                className={styles.modalMapFrame}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
              <button
                onClick={handleUseCurrentLocation}
                className={styles.useLocationBtn}
                style={{
                  position: "absolute",
                  bottom: "20px",
                  right: "20px",
                  backgroundColor: "#fff",
                  color: "#000",
                  padding: "10px 15px",
                  border: "none",
                  borderRadius: "30px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                }}
              >
                <FaLocationArrow /> Use My Location
              </button>
            </div>
            <div className={styles.modalFormColumn}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {editingId ? "Edit Address" : "Create New Address"}
                </h2>
                <FaTimes
                  className={styles.closeIcon}
                  onClick={() => setShowAddressModal(false)}
                  style={{ cursor: "pointer", fontSize: "1.2rem" }}
                />
              </div>
              <form onSubmit={handleSaveAddress} className={styles.addressForm}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Address</label>
                  <div className={styles.autocompleteWrapper}>
                    <input
                      required
                      type="text"
                      placeholder={
                        selectedMethodId === "delivery_us"
                          ? "Enter US City/State"
                          : selectedMethodId === "delivery_hawassa"
                          ? "Enter Hawassa Location"
                          : "Start typing location (e.g. Goro)"
                      }
                      className={styles.formInput}
                      value={newAddress.address}
                      onChange={handleAddressSearch}
                      autoComplete="off"
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <ul className={styles.suggestionsList}>
                        {suggestions.map((loc, index) => (
                          <li
                            key={index}
                            className={styles.suggestionItem}
                            onClick={() => handleSuggestionClick(loc)}
                          >
                            <strong>{loc}</strong>
                            <span
                              style={{
                                display: "block",
                                fontSize: "0.8em",
                                color: "#888",
                              }}
                            >
                              {loc}, Addis Ababa
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Full Name</label>
                  <input
                    required
                    type="text"
                    placeholder="First and Last Name"
                    className={`${styles.formInput} ${
                      errors.fullName ? styles.errorInput : ""
                    }`}
                    value={newAddress.fullName}
                    onChange={(e) => {
                      setNewAddress({
                        ...newAddress,
                        fullName: e.target.value,
                      });
                      if (errors.fullName)
                        setErrors({ ...errors, fullName: "" });
                    }}
                  />
                  {errors.fullName && (
                    <p className={styles.errorMessage}>{errors.fullName}</p>
                  )}
                </div>
                <div className={styles.phoneGroup}>
                  <div className={styles.codeSelectWrapper}>
                    <label className={styles.formLabel}>Code</label>
                    <div
                      className={styles.formInput}
                      style={{
                        backgroundColor: "#222",
                        color: "#ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #333",
                        fontSize: "0.9rem",
                      }}
                    >
                      {selectedMethodId === "delivery_us" ? "🇺🇸 +1" : "🇪🇹 +251"}
                    </div>
                  </div>
                  <div className={styles.phoneInputWrapper}>
                    <label className={styles.formLabel}>Phone Number</label>
                    <input
                      required
                      type="tel"
                      placeholder={
                        selectedMethodId === "delivery_us"
                          ? "1234567890"
                          : "911223344"
                      }
                      className={styles.formInput}
                      value={newAddress.phone}
                      onChange={handlePhoneChange}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Home number</label>
                  <input
                    type="text"
                    placeholder="House No."
                    className={styles.formInput}
                    value={newAddress.homeNumber}
                    onChange={(e) =>
                      setNewAddress({
                        ...newAddress,
                        homeNumber: e.target.value,
                      })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Detail</label>
                  <input
                    required
                    type="text"
                    placeholder="Specific location details"
                    className={styles.formInput}
                    value={newAddress.detail}
                    onChange={handleDetailChange}
                  />
                </div>
                <button type="submit" className={styles.submitBtn}>
                  {editingId ? "Update Address" : "Create Address"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      <div className={styles.contentWrapper}>
        <h1 className={styles.pageTitle}>Order Information</h1>
        <div className={styles.layoutGrid}>
          <div className={styles.column}>
            <div className={styles.methodsGrid}>
              {deliveryMethods.map((group, idx) => (
                <div key={idx} className={styles.methodGroup}>
                  <h3 className={styles.groupTitle}>{group.category}</h3>
                  <p className={styles.groupDesc}>
                    {group.desc || "Select this option."}
                  </p>
                  <div className={styles.optionsList}>
                    {group.options.map((option) => {
                      const isSelected = selectedMethodId === option.id;
                      return (
                        <div
                          key={option.id}
                          onClick={() => {
                            setSelectedMethodId(option.id);
                            setSelectedAddressId(null);
                          }}
                          className={`${styles.optionCard} ${
                            isSelected ? styles.selected : ""
                          }`}
                        >
                          <div
                            className={styles.optionIconWrapper}
                            style={
                              option.customIcon
                                ? {
                                    padding: 0,
                                    border: "none",
                                    overflow: "hidden",
                                  }
                                : {}
                            }
                          >
                            {option.customIcon ? (
                              <img
                                src={option.customIcon}
                                alt="icon"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                  display: "block",
                                }}
                              />
                            ) : (
                              option.icon
                            )}
                          </div>
                          <div className={styles.optionText}>
                            <h4 className={styles.optionTitle}>
                              {option.title}
                            </h4>
                            <p className={styles.optionSubtitle}>
                              {option.subtitle}
                            </p>
                          </div>
                          <div className={styles.checkIconWrapper}>
                            {isSelected ? (
                              <FaCheckCircle color="#fff" size={20} />
                            ) : (
                              <FaCircle color="#333" size={20} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.panel}>
              {selectedMethodId === "pickup" && (
                <>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>
                      Dukamo Pickup Address
                    </h3>
                  </div>
                  <div className={styles.optionsList}>
                    {pickupLocations.map((loc) => {
                      const isLocSelected = selectedLocationId === loc.id;
                      return (
                        <div
                          key={loc.id}
                          onClick={() => setSelectedLocationId(loc.id)}
                          className={`${styles.optionCard} ${
                            isLocSelected ? styles.selected : ""
                          }`}
                        >
                          <div
                            className={styles.checkIconWrapper}
                            style={{ marginRight: "15px", marginLeft: "0" }}
                          >
                            {isLocSelected ? (
                              <FaCheckCircle size={20} />
                            ) : (
                              <FaCircle color="#333" size={20} />
                            )}
                          </div>
                          <div className={styles.optionText}>
                            <h4 className={styles.optionTitle}>{loc.title}</h4>
                            <p className={styles.optionSubtitle}>
                              {loc.address}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {["delivery_addis", "delivery_hawassa", "delivery_us"].includes(
                selectedMethodId
              ) && (
                <>
                  <div className={styles.savedLocationsHeader}>
                    <h3 className={styles.sectionTitle}>Saved Location</h3>
                    <button
                      onClick={openNewAddressModal}
                      className={styles.createAddressBtn}
                    >
                      <FaPlus style={{ marginRight: "5px" }} /> New
                    </button>
                  </div>
                  {filteredAddresses.length === 0 ? (
                    <div className={styles.emptyAddressBox}>
                      No saved addresses for selected method. Create one above.
                    </div>
                  ) : (
                    <div className={styles.optionsList}>
                      {filteredAddresses.map((addr) => {
                        const isAddrSelected = selectedAddressId === addr.id;
                        return (
                          <div
                            key={addr.id}
                            onClick={() => setSelectedAddressId(addr.id)}
                            className={`${styles.optionCard} ${
                              isAddrSelected ? styles.selected : ""
                            }`}
                            style={{
                              position: "relative",
                              paddingRight: "80px",
                            }}
                          >
                            <div
                              className={styles.checkIconWrapper}
                              style={{ marginRight: "15px", marginLeft: "0" }}
                            >
                              {isAddrSelected ? (
                                <FaCheckCircle color="#fff" size={20} />
                              ) : (
                                <FaCircle color="#333" size={20} />
                              )}
                            </div>
                            <div className={styles.optionText}>
                              <h4 className={styles.optionTitle}>
                                {addr.addressName}
                              </h4>
                              <p className={styles.optionSubtitle}>
                                {addr.detail}, {addr.homeNumber}
                              </p>
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                right: "20px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                display: "flex",
                                gap: "12px",
                                zIndex: 10,
                              }}
                            >
                              <FaEdit
                                className={styles.actionIcon}
                                color="#ccc"
                                size={14}
                                title="Edit"
                                onClick={(e) => handleEditAddress(e, addr)}
                              />
                              <FaTrash
                                className={styles.actionIcon}
                                color="#ff4d4d"
                                size={14}
                                title="Delete"
                                onClick={(e) => handleDeleteAddress(e, addr.id)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={styles.continueContainer}>
              <button onClick={handleContinue} className={styles.continueBtn}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default DeliveryOptionsPage;
